import { Button, Card, CardHeader, CardPreview, Text, Tooltip } from "@fluentui/react-components";
import { IFinOpsBaseline, IFinOpsBudgetAllocationCombined } from "../../../models/FinOpsBudgets";
import { Info16Regular, QuestionCircle16Regular } from "@fluentui/react-icons";

import React from "react";
import { get } from "lodash";
import { getUTCMonthStartTime } from "../../../utils/BudgetUtils";
import styles from "./BudgetOverviewCard.less";

type BudgetOverviewCardsProps = {
    currentFiscalYear: string;
    previousFiscalYear: string;
    currentMonth: Date;
    data: IFinOpsBudgetAllocationCombined[];
    baselineData: IFinOpsBaseline[];
    isLoadingBudgetData: boolean;
    currencyFormatter: (value: number) => string;
    viewType?: "overview" | "detail";
};

export const BudgetOverviewCards: React.FC<BudgetOverviewCardsProps> = ({
    currentFiscalYear,
    previousFiscalYear,
    currentMonth,
    data,
    baselineData,
    isLoadingBudgetData,
    currencyFormatter,
    viewType = "overview",
}) => {
  const containerClass =
    viewType === "overview"
      ? styles.overviewCardContainer
      : styles.detailCardContainer;

  const cardClass =
    viewType === "overview"
      ? styles.overViewBudgetCard
      : styles.detailBudgetCard;

  const titleClass =
    viewType === "overview"
      ? styles.overViewBudgetCardTitle
      : styles.detailBudgetCardTitle;

  const valueClass =
    viewType === "overview"
      ? styles.overViewBudgetCardValue
      : styles.detailBudgetCardValue;

  const deduplicateData = (
    data: IFinOpsBudgetAllocationCombined[]
  ): IFinOpsBudgetAllocationCombined[] => {
    const seen = new Map<string, IFinOpsBudgetAllocationCombined>();

    for (const item of data) {
      const key = `${item.month}|${item.serviceId}|${item.category}|${item.type}`;
      if (!seen.has(key)) {
        seen.set(key, item);
      }
    }

    return Array.from(seen.values());
  };

  const deduplicateBaseline = (
    data: IFinOpsBaseline[]
  ): IFinOpsBaseline[] => {
    const seen = new Map<string, IFinOpsBaseline>();

    for (const item of data) {
      const key = `${item.month}|${item.serviceId}|${item.category}|${item.type}`;
      if (!seen.has(key)) {
        seen.set(key, item);
      }
    }

    return Array.from(seen.values());
  };

  const dedupedData = deduplicateData(data);
  const dedupedBaseline = deduplicateBaseline(baselineData);

    // Last fiscal year Spend
    const lastFiscalYearSpend = dedupedBaseline.reduce((sum, d) => sum + (d.cost ?? 0), 0);

    // Current fiscal year Spend
    const currentSpend = dedupedData
        .filter((d) => {
          if (!d.month) {
            return false;
          }
          return getUTCMonthStartTime(d.month) < getUTCMonthStartTime(currentMonth);
        })
        .reduce((sum, d) => sum + (d.cost ?? 0), 0);

    // Future Forecast
    const futureForecast = dedupedData
        .filter((d) => {
        if (!d.month) {
          return false;
        }
        return getUTCMonthStartTime(d.month) >= getUTCMonthStartTime(currentMonth)
        })
        .reduce((sum, d) => sum + (d.cost ?? 0), 0);

    // Budget
    const budget = dedupedData.reduce((sum, d) => sum + (d.budget ?? 0), 0);

    // Gap
    const gap = futureForecast + currentSpend - budget;

  const renderCard = (title: string, value: number, action?: React.ReactNode) => (
    <Card className={cardClass}>
      <CardHeader header={<Text className={titleClass}>{title}</Text>} action={action ? { children: action } : undefined}/>
      <CardPreview>
        <Text className={valueClass}>
          {isLoadingBudgetData ? "Loading..." : currencyFormatter(value)}
        </Text>
      </CardPreview>
    </Card>
  );

  return (
    <div className={containerClass}>
      {renderCard(`${previousFiscalYear} Baseline`, lastFiscalYearSpend)}
      {renderCard(`${currentFiscalYear} Budget Ask`, budget)}
      {renderCard(`${currentFiscalYear} Current Usage`, currentSpend)}
      {renderCard(
        `${currentFiscalYear} Future Forecast`,
        futureForecast,
        <Tooltip 
          content= {<>Learn more about forecast: {" "} <a href="https://o365exchange.visualstudio.com/O365%20Core/_wiki/wikis/O365%20Core.wiki/541820/FinOps-Forecast-Logic" target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "underline" }}>
            FinOps Wiki - FinOps Forecast Logic
          </a></>}
          relationship="label">
          <Button
            appearance="subtle"
            size="medium"
            icon={<QuestionCircle16Regular />}
          />
        </Tooltip>
      )}
      {renderCard(`${currentFiscalYear} Budget Gap`, gap)}
    </div>
  );
};
